import type { TFunction } from 'i18next';

import {
  GetProjectPotentialPipelineTransitionsQuery,
  PipelineGuardSimpleConditionType,
} from '@/gql/graphql';
import {
  PipelineGuardResultGroup,
  PipelineGuardResultGroups,
} from '@payaca/components/pipelineStage/GuardResultDisplay';
import { getGuardMustBe, getGuardMustNotBe } from './pipelineGuardDescriptions';

type PotentialPipelineTransition =
  GetProjectPotentialPipelineTransitionsQuery['projectById']['potentialPipelineTransitions'][number];

export type GuardResult = NonNullable<
  PotentialPipelineTransition['guardResult']
>;

/**
 * List of all simple pipeline guard condition types.
 * Used to enumerate available conditions for UI dropdowns.
 */
export const PIPELINE_GUARD_SIMPLE_CONDITION_TYPES: PipelineGuardSimpleConditionType[] =
  [
    'PROJECT_HAS_ACCEPTED_PROPOSAL',
    'PROJECT_HAS_ACTIVE_SENT_PROPOSAL',
    'PROJECT_HAS_PAID_DEPOSIT_INVOICE',
    'PROJECT_ACCEPTED_VALUE_IS_FULLY_INVOICED',
    'PROJECT_ACCEPTED_VALUE_IS_FULLY_PAID',
    'PROJECT_HAS_EXPIRED_PROPOSAL',
    'PROJECT_HAS_DECLINED_PROPOSAL',
  ];

export function getDisallowedTransitionsSet(
  transitions: PotentialPipelineTransition[]
): Set<string> {
  return new Set(
    transitions.filter((t) => !t.isAllowed).map((t) => t.targetStage.publicId)
  );
}

export function getTransitionGuardResult(
  t: TFunction,
  transitions: PotentialPipelineTransition[],
  stagePublicId: string
): PipelineGuardResultGroups | undefined {
  const transition = transitions.find(
    (tr) => tr.targetStage.publicId === stagePublicId
  );
  if (!transition || transition.isAllowed || !transition.guardResult) {
    return undefined;
  }
  return getDisplayGroups(t, transition.guardResult);
}

/**
 * Extracts display groups from a guard result tree. Each group represents
 * an OR condition, containing AND conditions as its children.
 */
export function getDisplayGroups(
  t: TFunction,
  result: GuardResult
): PipelineGuardResultGroups {
  const { guard, children } = result;

  switch (guard.__typename) {
    case 'PipelineGuardOr':
      // Each OR child becomes a separate group
      return {
        or: children?.flatMap((child) => getDisplayGroups(t, child).or) ?? [],
      };

    case 'PipelineGuardAnd':
      // AND children become conditions within a single group
      return {
        or: [
          {
            and:
              children?.flatMap((child) => getConditions(t, child).and) ?? [],
          },
        ],
      };

    case 'PipelineGuardNot':
    case 'PipelineGuardSimpleCondition':
    case 'PipelineGuardProjectHasTagCondition':
    case 'PipelineGuardCustomFieldCondition':
      // Single condition becomes a group with one condition
      return { or: [getConditions(t, result)] };

    case 'PipelineGuardPass':
    default:
      return { or: [] };
  }
}

/**
 * Extracts individual conditions from a guard result node.
 * Handles NOT wrappers and simple conditions.
 */
function getConditions(
  t: TFunction,
  result: GuardResult
): PipelineGuardResultGroup {
  const { guard, isSatisfied, children } = result;

  switch (guard.__typename) {
    case 'PipelineGuardSimpleCondition':
      return {
        and: [
          {
            description: getGuardMustBe(t, guard.type),
            isSatisfied,
          },
        ],
      };

    case 'PipelineGuardProjectHasTagCondition':
      return {
        and: [
          {
            description: t('pipelineGuards.conditions.PROJECT_HAS_TAG.mustBe', {
              tagName: guard.tag?.name,
            }),
            isSatisfied,
          },
        ],
      };

    case 'PipelineGuardCustomFieldCondition':
      return {
        and: [
          {
            description: describeCustomFieldCondition(t, guard),
            isSatisfied,
          },
        ],
      };

    case 'PipelineGuardNot':
      // For NOT, get the child's description but use the NOT's satisfaction status
      if (children && children.length === 1) {
        const childGuard = children[0].guard;
        if (childGuard.__typename === 'PipelineGuardSimpleCondition') {
          return {
            and: [
              {
                description: getGuardMustNotBe(t, childGuard.type),
                isSatisfied,
              },
            ],
          };
        }
        if (childGuard.__typename === 'PipelineGuardProjectHasTagCondition') {
          return {
            and: [
              {
                description: t(
                  'pipelineGuards.conditions.PROJECT_HAS_TAG.mustNotBe',
                  { tagName: childGuard.tag?.name }
                ),
                isSatisfied,
              },
            ],
          };
        }
      }
      console.warn(
        'Unexpected guard structure: NOT guard without complex child'
      );
      return { and: [] };

    case 'PipelineGuardAnd':
      return {
        and: children?.flatMap((child) => getConditions(t, child).and) ?? [],
      };

    case 'PipelineGuardOr':
      console.warn('Unexpected guard structure: OR guard not at top level');
      return { and: [] };

    case 'PipelineGuardPass':
      console.warn('Unexpected guard structure: PASS guard not at top level');
      return { and: [] };
  }
}

/**
 * Localised summary for a customFieldCondition guard result. Each operator
 * has its own summary template under
 * `pipelineGuards.conditions.CUSTOM_FIELD_CONDITION.summary.{operator}`.
 * Equality operators substitute `{{value}}`; the rest only `{{field}}`.
 */
function describeCustomFieldCondition(
  t: TFunction,
  guard: Extract<
    GuardResult['guard'],
    { __typename: 'PipelineGuardCustomFieldCondition' }
  >
): string {
  const field = guard.field?.label ?? guard.fieldPath;
  const summaryKey = (
    operator:
      | 'equals'
      | 'notEquals'
      | 'isSet'
      | 'isNotSet'
      | 'allChildrenAreSet'
      | 'allChildrenAreNotSet'
      | 'anyChildIsSet'
  ) =>
    `pipelineGuards.conditions.CUSTOM_FIELD_CONDITION.summary.${operator}` as const;

  switch (guard.predicate.__typename) {
    case 'EqualsCustomFieldConditionPredicate':
      return t(summaryKey('equals'), {
        field,
        value: formatComparisonEntityOrValue(
          guard.predicate.comparisonEntity,
          guard.predicate.comparisonValue
        ),
      });
    case 'NotEqualsCustomFieldConditionPredicate':
      return t(summaryKey('notEquals'), {
        field,
        value: formatComparisonEntityOrValue(
          guard.predicate.comparisonEntity,
          guard.predicate.comparisonValue
        ),
      });
    case 'IsSetCustomFieldConditionPredicate':
      return t(summaryKey('isSet'), { field });
    case 'IsNotSetCustomFieldConditionPredicate':
      return t(summaryKey('isNotSet'), { field });
    case 'AllChildrenAreSetCustomFieldConditionPredicate':
      return t(summaryKey('allChildrenAreSet'), { field });
    case 'AllChildrenAreNotSetCustomFieldConditionPredicate':
      return t(summaryKey('allChildrenAreNotSet'), { field });
    case 'AnyChildIsSetCustomFieldConditionPredicate':
      return t(summaryKey('anyChildIsSet'), { field });
    default:
      return field;
  }
}

function formatComparisonValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'string') return `"${value}"`;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  return JSON.stringify(value);
}

function formatComparisonEntityOrValue(
  entity: { fullName: string } | null | undefined,
  value: unknown
): string {
  if (entity?.fullName) return entity.fullName;
  return formatComparisonValue(value);
}
